/**
 * Histórico de importações: cada arquivo que entrou, quem importou, o período,
 * a situação (em uso, substituída ou desfeita), o relatório da prévia e o
 * botão "Desfazer" (as vendas daquela importação deixam de valer e a
 * importação anterior do mesmo período volta a valer). Embaixo, as carteiras
 * de clientes importadas (cada uma é a foto de uma data), também com "Desfazer".
 *
 * Lê: api.importacoes / api.importacao.
 * Usado por: App.tsx (rota #/importacoes).
 */
import { useEffect, useState } from 'react';
import { Aviso, ErroNaTela } from '../componentes/basicos.tsx';
import { api, ErroApi } from '../dados/api.ts';
import type { Carteira, Importacao } from '../dominio/tipos.ts';
import { formatarData, formatarDataHora, formatarMoeda, NOME_DO_SISTEMA } from '../formatacao.ts';
import { linkPara } from '../rotas.ts';

function situacao(importacao: Importacao): { texto: string; classe: string } {
  if (importacao.desfeitaEm) return { texto: 'Desfeita', classe: 'etiqueta etiqueta-apagada' };
  if (importacao.vendasVigentes === 0) return { texto: 'Substituída', classe: 'etiqueta etiqueta-apagada' };
  if (importacao.vendasVigentes < importacao.linhasUsadas) return { texto: 'Em uso (em parte)', classe: 'etiqueta etiqueta-bom' };
  return { texto: 'Em uso', classe: 'etiqueta etiqueta-bom' };
}

export function Importacoes() {
  const [lista, setLista] = useState<Importacao[] | null>(null);
  const [erro, setErro] = useState<ErroApi | null>(null);
  const [aberta, setAberta] = useState<{ id: number; relatorio: Record<string, unknown> } | null>(null);
  const [versao, setVersao] = useState(0);

  useEffect(() => {
    api
      .importacoes()
      .then(({ importacoes }) => setLista(importacoes))
      .catch((falha: unknown) => setErro(falha instanceof ErroApi ? falha : new ErroApi('APP', String(falha), 0, null)));
  }, [versao]);

  async function abrir(id: number) {
    if (aberta?.id === id) {
      setAberta(null);
      return;
    }
    try {
      const { importacao } = await api.importacao(id);
      setAberta({ id, relatorio: importacao.relatorio });
    } catch (falha) {
      setErro(falha instanceof ErroApi ? falha : new ErroApi('APP', String(falha), 0, null));
    }
  }

  async function desfazer(importacao: Importacao) {
    const ok = window.confirm(
      `Desfazer a importação de "${importacao.nomeArquivo}" (${formatarData(importacao.periodoInicio)} a ${formatarData(importacao.periodoFim)})?\n\nAs vendas dela deixam de contar e a importação anterior do mesmo período volta a valer. Nada é apagado.`,
    );
    if (!ok) return;
    try {
      await api.desfazerImportacao(importacao.id);
      setVersao((v) => v + 1);
    } catch (falha) {
      setErro(falha instanceof ErroApi ? falha : new ErroApi('APP', String(falha), 0, null));
    }
  }

  return (
    <div className="pagina">
      <div className="pagina-titulo">
        <div>
          <h1>Importações</h1>
          <p>Cada arquivo que entrou no sistema. Para cada marca e período, vale a importação mais nova.</p>
        </div>
        <a className="botao" href={linkPara({ tela: 'importar' })}>
          Importar arquivo
        </a>
      </div>
      {erro ? <ErroNaTela erro={erro} /> : null}
      {lista && lista.length === 0 ? <Aviso tipo="info">Nenhum arquivo importado ainda.</Aviso> : null}
      {lista && lista.length > 0 ? (
        <section className="cartao">
          <div className="tabela-rolavel">
            <table className="tabela">
              <thead>
                <tr>
                  <th>Importado em</th>
                  <th>Arquivo</th>
                  <th>Sistema</th>
                  <th>Marca</th>
                  <th>Período</th>
                  <th className="numero">Notas</th>
                  <th className="numero">Ignoradas</th>
                  <th className="numero">Líquido</th>
                  <th>Situação</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {lista.map((importacao) => {
                  const estado = situacao(importacao);
                  return (
                    <tr key={importacao.id}>
                      <td>
                        {formatarDataHora(importacao.criadoEm)}
                        <br />
                        <span className="texto-apagado">por {importacao.criadoPor}</span>
                      </td>
                      <td>{importacao.nomeArquivo}</td>
                      <td>{NOME_DO_SISTEMA[importacao.fonte]}</td>
                      <td>{importacao.marca ?? <span className="texto-apagado">geral</span>}</td>
                      <td>
                        {formatarData(importacao.periodoInicio)} a {formatarData(importacao.periodoFim)}
                      </td>
                      <td className="numero">{importacao.linhasUsadas}</td>
                      <td className="numero">{importacao.linhasIgnoradas}</td>
                      <td className="numero">{formatarMoeda(importacao.totalLiquido)}</td>
                      <td>
                        <span className={estado.classe}>{estado.texto}</span>
                        {importacao.desfeitaEm ? (
                          <>
                            <br />
                            <span className="texto-apagado">
                              {formatarDataHora(importacao.desfeitaEm)} por {importacao.desfeitaPor}
                            </span>
                          </>
                        ) : null}
                      </td>
                      <td>
                        <div className="linha-campos">
                          <button type="button" className="botao-secundario" onClick={() => abrir(importacao.id)}>
                            {aberta?.id === importacao.id ? 'Fechar' : 'Relatório'}
                          </button>
                          {!importacao.desfeitaEm ? (
                            <button type="button" className="botao-perigo" onClick={() => desfazer(importacao)}>
                              Desfazer
                            </button>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}
      {aberta ? <RelatorioSalvo relatorio={aberta.relatorio} /> : null}
      <CarteirasImportadas />
    </div>
  );
}

/** As fotos da carteira de clientes (só contagens), com "Desfazer". */
function CarteirasImportadas() {
  const [lista, setLista] = useState<Carteira[] | null>(null);
  const [erro, setErro] = useState<ErroApi | null>(null);
  const [versao, setVersao] = useState(0);

  useEffect(() => {
    api
      .carteiras()
      .then(({ carteiras }) => setLista(carteiras))
      .catch((falha: unknown) => setErro(falha instanceof ErroApi ? falha : new ErroApi('APP', String(falha), 0, null)));
  }, [versao]);

  async function desfazer(carteira: Carteira) {
    const ok = window.confirm(
      `Desfazer a carteira de ${formatarData(carteira.dataReferencia)} ("${carteira.nomeArquivo}")?\n\nEla deixa de valer para o % da carteira e a anterior volta a valer. Nada é apagado.`,
    );
    if (!ok) return;
    try {
      await api.desfazerCarteira(carteira.id);
      setVersao((v) => v + 1);
    } catch (falha) {
      setErro(falha instanceof ErroApi ? falha : new ErroApi('APP', String(falha), 0, null));
    }
  }

  if (erro) return <ErroNaTela erro={erro} />;
  if (!lista || lista.length === 0) return null;
  return (
    <section className="cartao pilha">
      <div>
        <h2>Carteiras de clientes</h2>
        <p className="subtitulo">
          Cada uma é a foto da carteira numa data. Para cada mês vale a mais recente até o fim do mês. Só a quantidade de clientes de cada vendedor fica guardada.
        </p>
      </div>
      <div className="tabela-rolavel">
        <table className="tabela">
          <thead>
            <tr>
              <th>Data da carteira</th>
              <th>Arquivo</th>
              <th className="numero">Clientes</th>
              <th>Importada em</th>
              <th>Situação</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {lista.map((carteira) => (
              <tr key={carteira.id}>
                <td>{formatarData(carteira.dataReferencia)}</td>
                <td>{carteira.nomeArquivo}</td>
                <td className="numero">{carteira.totalClientes}</td>
                <td>
                  {formatarDataHora(carteira.criadoEm)}
                  <br />
                  <span className="texto-apagado">por {carteira.criadoPor}</span>
                </td>
                <td>
                  <span className={carteira.desfeitaEm ? 'etiqueta etiqueta-apagada' : 'etiqueta etiqueta-bom'}>{carteira.desfeitaEm ? 'Desfeita' : 'Guardada'}</span>
                </td>
                <td>
                  {!carteira.desfeitaEm ? (
                    <button type="button" className="botao-perigo" onClick={() => desfazer(carteira)}>
                      Desfazer
                    </button>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

interface RelatorioGuardado {
  aba?: string;
  linhasDeDados?: number;
  conferencia?: { bate: boolean | null; arquivo: { liquido: number } | null; lido: { liquido: number } };
  avisos?: Array<{ codigo: string; mensagem: string }>;
  ignoradas?: Array<{ linha: number; codigo: string; motivo: string; resumo: string }>;
  vendedores?: Array<{ codigo: string; nomeNoArquivo: string; representante: string | null; notas: number; liquido: number }>;
}

/** O relatório que a gerente viu na prévia, guardado junto com a importação. */
function RelatorioSalvo({ relatorio }: { relatorio: Record<string, unknown> }) {
  const r = relatorio as RelatorioGuardado;
  return (
    <section className="cartao pilha">
      <h2>Relatório da importação</h2>
      <p className="subtitulo">
        Aba "{r.aba ?? '?'}" · {r.linhasDeDados ?? '?'} linhas na planilha · conferência com a linha de totais:{' '}
        {r.conferencia?.bate === true ? 'bateu' : r.conferencia?.bate === false ? 'NÃO bateu' : 'sem linha de totais'}
      </p>
      {(r.avisos ?? []).map((aviso, i) => (
        <Aviso key={i} tipo="alerta">
          {aviso.mensagem} <span className="codigo-erro">[{aviso.codigo}]</span>
        </Aviso>
      ))}
      <div className="grade-duas">
        <div className="tabela-rolavel">
          <table className="tabela">
            <thead>
              <tr>
                <th>Código</th>
                <th>Nome no arquivo</th>
                <th>Representante</th>
                <th className="numero">Notas</th>
                <th className="numero">Líquido</th>
              </tr>
            </thead>
            <tbody>
              {(r.vendedores ?? []).map((v) => (
                <tr key={`${v.codigo}-${v.representante ?? ''}`}>
                  <td>{v.codigo}</td>
                  <td>{v.nomeNoArquivo}</td>
                  <td>{v.representante ?? <span className="etiqueta etiqueta-alerta">sem cadastro</span>}</td>
                  <td className="numero">{v.notas}</td>
                  <td className="numero">{formatarMoeda(v.liquido)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="tabela-rolavel">
          <table className="tabela">
            <thead>
              <tr>
                <th>Linha</th>
                <th>Código</th>
                <th>Motivo</th>
              </tr>
            </thead>
            <tbody>
              {(r.ignoradas ?? []).map((item) => (
                <tr key={`${item.linha}-${item.codigo}`}>
                  <td>{item.linha}</td>
                  <td className="codigo-erro">{item.codigo}</td>
                  <td>
                    {item.motivo}
                    {item.resumo ? <span className="texto-apagado"> · {item.resumo}</span> : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}
